import {EmptyStatusProps} from 'platform/components';

import {ReactElement} from 'react';

import {Nullish, TestIdProps} from 'shared/web';

import {ActionCallback} from './ActionCallback';
import {ColDef} from './AgGridTypes';
import {DataGridBehaviorOverrides} from './Api';
import {ConnectionOverride} from './ConnectionOverride';
import {ExternalFilters} from './ExternalFilters';
import {FormToDataGridConnectorProps} from './FormToDataGridConnectorProps';
import {LinkClickOverride} from './LinkClickOverride';
import {QueryFilterObject} from './QueryFilterObject';
import {RowData} from './RowData';

type IfNever<T, Y, N> = [T] extends [never] ? Y : N;

export type DataGridProps<T extends readonly string[] = readonly string[]> = IfNever<
  T,
  {predefinedColumns?: T},
  {predefinedColumns: T}
> & {
  gridCode: string;
  /* Identifies specific virtual preset */
  presetInstanceId?: string;
  /* Allows set custom message or actions for empty status */
  emptyState?: Omit<EmptyStatusProps, 'variant'>;
  /* Handles click on any row action */
  actionCallback?: ActionCallback<NoInfer<T>>;
  /* Callback that is invoked every time a row selection is changed */
  onRowSelectionChange?: (selectedRowsData: Array<RowData>) => void;
  /* Identifies element to be used as a container for the external filter */
  externalFilterId?: string;
  externalFilterResourceId?: string | Nullish;
  externalFilterRecordId?: string | Nullish;
  /* Callback that is invoked every time external filter is changed */
  onExternalFilterChange?: (filterValues: ExternalFilters) => void;
  /* Set to true to have the detail grid dynamically change its height to fit its rows */
  autoHeight?: boolean;
  /**
   * Function that can be used to modify request for data.
   * It gets filter query from dataGrid and returns modified query, modified query is used to filter data on BE.
   * Invalid usage:
   *  You got task where you have to create filters outside the DG component. Do not do this, it goes against
   *  main DG idea. Return such task to PO, APO or designer.
   * Valid usage:
   *  You have DG where you want to pass current user id into data query. Such things cannot be done by supplied filters.
   */
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
  /**
   * @deprecated Use this property as last resort!
   * This property can be used to change DataGridSettings object provided by API.
   * Fetched settings are override with object from this property (via spread operator).
   * Invalid usage:
   *  You got task where you have to change settings or behaviour of DG (eg. show preset panel). Do not do this, it goes
   *  against main DG idea. Return such task to PO or APO with information that such changes should be done on BE (and
   *  updated in documentation)
   * Valid usage:
   *  You have to quickly fix behaviour of DG on production while no BE developer is present to do this fix. Also
   *  you will urge BE devs to solve this and after that remove usage of this property ASAP!
   */
  _useAsLastResort_definitionBehaviorOverrides?: DataGridBehaviorOverrides;
  /**
   * @deprecated Use this property as last resort!
   * This property can be used to change column definition that is passed to Ag-Grid component.
   * Final colDefs (that are created form API responses) are override with object from this property (via spread operator).
   * Invalid usage:
   *  - You got task in which you should create custom column renderer (lets say IconRenderer, GreenPriceRenderer etc.)
   *  - You need to change text of tooltip on sole cells
   *  - You have to display value in cell that are not provided by DG API
   *  Do not do this, it goes against main DG idea. Return such tasks to PO or APO with request that platform team creates
   *  systematic solution to your task.
   * Valid usage:
   *  You have to hotfix cell behaviour while platform team works on valid solution. You will remove usage of this
   *  property ASAP!
   */
  _useAsLastResort_colDefOverrides?: Record<string, ColDef>;
  /**
   * @deprecated Use this property as last resort, its intended for dev purposes!
   * This property can be used to change base url and Authorization header that is used for DG connection.
   * Invalid usage:
   *  - You connecting DG in new aplication, you should provide this url by enviroment variables
   *  Do not do this, it goes against main DG idea.
   * Valid usage:
   *  You are preparing new DG, which is not prepared on FE side.
   */
  _useAsLastResort_connectionOverride?: ConnectionOverride;
  _useAsLastResort_linkClickOverride?: LinkClickOverride;
  onFilterChanged?: (filters: any) => void;
  filterComponent?: (props: FormToDataGridConnectorProps) => ReactElement;
  /* When true, disables all interactive actions including row actions, bulk actions, context menus, and cell editing */
  disableAllActions?: boolean;
  /* Initial value for the quick filter/smart search */
  initialSmartSearch?: string;
} & TestIdProps;
